/** `failed` names the step that failed ("Couldn't read a file"), so an error never
 * borrows the wording of a step that succeeded. */
import { ACTIVITY_VERB_COPY } from './activity-verbs-locales';

/** `counted` is a repeat, in CLDR plural forms with the number where the
 * language puts it: "one:Read {count} file|other:Read {count} files". */
export type ActivityVerb = { running: string; done: string; counted?: string; failed?: string };

export const ACTIVITY_VERBS: Readonly<Record<string, ActivityVerb>> = {
  search_context: {
    running: 'Searching the company',
    done: 'Searched the company',
    failed: "Couldn't search the company",
  },
  // search_company_knowledge and find_company_work are no longer native
  // tools (search_context replaced both); earlier chats still show them.
  search_company_knowledge: {
    running: 'Searching company knowledge',
    done: 'Searched company knowledge',
    failed: "Couldn't search company knowledge",
  },
  read_company_context: {
    running: 'Reading company context',
    done: 'Read company context',
    failed: "Couldn't read company context",
  },
  find_company_work: {
    running: 'Looking through company work',
    done: 'Looked through company work',
    failed: "Couldn't look through company work",
  },
  read_company_meeting: {
    running: 'Reading a meeting',
    done: 'Read a meeting',
    counted: 'one:Read {count} meeting|other:Read {count} meetings',
    failed: "Couldn't read a meeting",
  },
  read_company_file: {
    running: 'Reading a file',
    done: 'Read a file',
    counted: 'one:Read {count} file|other:Read {count} files',
    failed: "Couldn't read a file",
  },
  search_company_file: {
    running: 'Searching a file',
    done: 'Searched a file',
    counted: 'one:Searched {count} file|other:Searched {count} files',
    failed: "Couldn't search a file",
  },
  read_company_source: {
    running: 'Reading a document',
    done: 'Read a document',
    counted: 'one:Read {count} document|other:Read {count} documents',
    failed: "Couldn't read a document",
  },
  // Storefront: Chat reads the company's shopping assistants and looks at one's preview.
  storefront_stores: {
    running: 'Checking your shopping assistants',
    done: 'Checked your shopping assistants',
    failed: "Couldn't check your shopping assistants",
  },
  render_storefront_preview: {
    running: 'Looking at the shopping assistant',
    done: 'Looked at the shopping assistant',
    failed: "Couldn't look at the shopping assistant",
  },
  storefront_conversations: {
    running: 'Reading shopper conversations',
    done: 'Read shopper conversations',
    failed: "Couldn't read shopper conversations",
  },
  read_knowledge_page: {
    running: 'Reading a knowledge page',
    done: 'Read a knowledge page',
    counted: 'one:Read {count} knowledge page|other:Read {count} knowledge pages',
    failed: "Couldn't read a knowledge page",
  },
  search_work_results: {
    running: 'Searching saved results',
    done: 'Searched saved results',
    failed: "Couldn't search saved results",
  },
  read_work_result: {
    running: 'Reading a saved result',
    done: 'Read a saved result',
    counted: 'one:Read {count} saved result|other:Read {count} saved results',
    failed: "Couldn't read a saved result",
  },
  read_previous_chat: {
    running: 'Reading an earlier chat',
    done: 'Read an earlier chat',
    counted: 'one:Read {count} earlier chat|other:Read {count} earlier chats',
    failed: "Couldn't read an earlier chat",
  },
  read_connected_document: {
    running: 'Reading a connected document',
    done: 'Read a connected document',
    counted: 'one:Read {count} connected document|other:Read {count} connected documents',
    failed: "Couldn't read a connected document",
  },
  read_url: {
    running: 'Reading a web page',
    done: 'Read a web page',
    counted: 'one:Read {count} web page|other:Read {count} web pages',
    failed: "Couldn't read a web page",
  },
  open_web: {
    running: 'Reading a web page',
    done: 'Read a web page',
    counted: 'one:Read {count} web page|other:Read {count} web pages',
    failed: "Couldn't read a web page",
  },
  view_image: {
    running: 'Looking at an image',
    done: 'Looked at an image',
    counted: 'one:Looked at {count} image|other:Looked at {count} images',
    failed: "Couldn't look at an image",
  },
  read_company_skill: {
    running: 'Checking Skills',
    done: 'Checked Skills',
    failed: "Couldn't check Skills",
  },
  list_company_skills: {
    running: 'Checking Skills',
    done: 'Checked Skills',
    failed: "Couldn't check Skills",
  },
  find_right_skill: {
    running: 'Finding the right Skills',
    done: 'Found Skills',
    failed: "Couldn't find Skills",
  },
  // Projects v1: the project's brief, Skills and reads-first, applied to a chat that lives in it.
  project_context: {
    running: 'Reading the project brief',
    done: 'Read the project brief',
    failed: "Couldn't read the project brief",
  },
  read_projects: {
    running: 'Checking projects',
    done: 'Checked projects',
    failed: "Couldn't check projects",
  },
  draft_project: {
    running: 'Drafting the project',
    done: 'Drafted the project',
    failed: "Couldn't draft the project",
  },
  update_project: {
    running: 'Preparing the project change',
    done: 'Prepared the project change',
    failed: "Couldn't prepare the project change",
  },
  move_chat_to_project: {
    running: 'Moving the chat',
    done: 'Moved the chat',
    failed: "Couldn't move the chat",
  },
  add_to_project: {
    running: 'Adding to the project',
    done: 'Added to the project',
    failed: "Couldn't add to the project",
  },
  remove_from_project: {
    running: 'Taking it out of the project',
    done: 'Took it out of the project',
    failed: "Couldn't take it out of the project",
  },
  suggest_project_move: {
    running: 'Checking where this chat belongs',
    done: 'Suggested a project',
    failed: "Couldn't suggest a project",
  },
  read_my_settings: {
    running: 'Checking your settings',
    done: 'Checked your settings',
    failed: "Couldn't check your settings",
  },
  read_account_overview: {
    running: 'Checking your workspace',
    done: 'Checked your workspace',
    failed: "Couldn't check your workspace",
  },
  list_team: {
    running: 'Checking the team',
    done: 'Checked the team',
    failed: "Couldn't check the team",
  },
  read_company_usage: {
    running: 'Checking usage',
    done: 'Checked usage',
    failed: "Couldn't check usage",
  },
  list_connection_tools: {
    running: 'Checking connected apps',
    done: 'Checked connected apps',
    failed: "Couldn't check connected apps",
  },
  read_connection_action: {
    running: 'Checking connected apps',
    done: 'Checked connected apps',
    failed: "Couldn't check connected apps",
  },
  list_custom_mcp_tools: {
    running: 'Checking connected apps',
    done: 'Checked connected apps',
    failed: "Couldn't check connected apps",
  },
  read_custom_mcp_action: {
    running: 'Checking connected apps',
    done: 'Checked connected apps',
    failed: "Couldn't check connected apps",
  },
  // DES1 HC-06: the company database reads. Named per connection in
  // activity-summary.ts when the turn's own list result names it.
  list_connection_reads: {
    running: 'Checking what it can read',
    done: 'Checked what it can read',
    failed: "Couldn't check what it can read",
  },
  read_connection: {
    running: 'Reading connected data',
    done: 'Read connected data',
    failed: "Couldn't read connected data",
  },
};

export type ActivityLocale =
  | 'bg'
  | 'cs'
  | 'da'
  | 'de'
  | 'el'
  | 'en'
  | 'es'
  | 'et'
  | 'fi'
  | 'fr'
  | 'ga'
  | 'hr'
  | 'hu'
  | 'it'
  | 'lt'
  | 'lv'
  | 'mt'
  | 'nl'
  | 'pl'
  | 'pt'
  | 'ro'
  | 'sk'
  | 'sl'
  | 'sv';

/** Locale overrides keep the line and canvas on one identifier-keyed registry. */
export const LOCALIZED_ACTIVITY_VERBS: Record<
  ActivityLocale,
  Partial<Record<string, ActivityVerb>>
> = {
  en: {},
  ...ACTIVITY_VERB_COPY,
  de: {
    ...ACTIVITY_VERB_COPY.de,
    search_context: {
      running: 'Durchsuche das Unternehmen',
      done: 'Unternehmen durchsucht',
      failed: 'Unternehmen konnte nicht durchsucht werden',
    },
  },
  sv: {
    ...ACTIVITY_VERB_COPY.sv,
    search_context: {
      running: 'Söker i företaget',
      done: 'Sökte i företaget',
      failed: 'Kunde inte söka i företaget',
    },
  },
};

/** HC-06 copy for the company database reads, in every app language. `{name}` is
 * the connection's own name from the turn's list result; inflected languages put
 * it after a colon so a name is never declined. `tables` is a formatCount form. */
export type ConnectionReadCopy = {
  listRunning: string;
  listDone: string;
  listFailed: string;
  readRunning: string;
  readDone: string;
  readFailed: string;
  namedRunning: string;
  namedDone: string;
  namedFailed: string;
  tablesCount: string;
};

export const CONNECTION_READ_COPY: Record<ActivityLocale, ConnectionReadCopy> = {
  bg: {
    listRunning: 'Проверка какво може да се чете',
    listDone: 'Проверено какво може да се чете',
    listFailed: 'Не можа да се провери какво може да се чете',
    readRunning: 'Четене на свързани данни',
    readDone: 'Свързаните данни са прочетени',
    readFailed: 'Свързаните данни не можаха да бъдат прочетени',
    namedRunning: 'Четене: {name}',
    namedDone: 'Прочетено: {name}',
    namedFailed: 'Не можа да се прочете: {name}',
    tablesCount: 'one:таблица|other:таблици',
  },
  cs: {
    listRunning: 'Ověřování, co lze číst',
    listDone: 'Ověřeno, co lze číst',
    listFailed: 'Nepodařilo se ověřit, co lze číst',
    readRunning: 'Čtení připojených dat',
    readDone: 'Připojená data přečtena',
    readFailed: 'Připojená data se nepodařilo přečíst',
    namedRunning: 'Čtení: {name}',
    namedDone: 'Přečteno: {name}',
    namedFailed: 'Nepodařilo se přečíst: {name}',
    tablesCount: 'one:tabulka|few:tabulky|many:tabulky|other:tabulek',
  },
  da: {
    listRunning: 'Tjekker, hvad den kan læse',
    listDone: 'Tjekkede, hvad den kan læse',
    listFailed: 'Kunne ikke tjekke, hvad den kan læse',
    readRunning: 'Læser forbundne data',
    readDone: 'Læste forbundne data',
    readFailed: 'Kunne ikke læse forbundne data',
    namedRunning: 'Læser {name}',
    namedDone: 'Læste {name}',
    namedFailed: 'Kunne ikke læse {name}',
    tablesCount: 'one:tabel|other:tabeller',
  },
  de: {
    listRunning: 'Prüfe, was gelesen werden kann',
    listDone: 'Geprüft, was gelesen werden kann',
    listFailed: 'Konnte nicht prüfen, was gelesen werden kann',
    readRunning: 'Lese verbundene Daten',
    readDone: 'Verbundene Daten gelesen',
    readFailed: 'Verbundene Daten konnten nicht gelesen werden',
    namedRunning: 'Lese {name}',
    namedDone: '{name} gelesen',
    namedFailed: '{name} konnte nicht gelesen werden',
    tablesCount: 'one:Tabelle|other:Tabellen',
  },
  el: {
    listRunning: 'Έλεγχος του τι μπορεί να διαβαστεί',
    listDone: 'Ελέγχθηκε τι μπορεί να διαβαστεί',
    listFailed: 'Δεν ήταν δυνατός ο έλεγχος του τι μπορεί να διαβαστεί',
    readRunning: 'Ανάγνωση συνδεδεμένων δεδομένων',
    readDone: 'Τα συνδεδεμένα δεδομένα διαβάστηκαν',
    readFailed: 'Δεν ήταν δυνατή η ανάγνωση συνδεδεμένων δεδομένων',
    namedRunning: 'Ανάγνωση: {name}',
    namedDone: 'Διαβάστηκε: {name}',
    namedFailed: 'Δεν ήταν δυνατή η ανάγνωση: {name}',
    tablesCount: 'one:πίνακας|other:πίνακες',
  },
  en: {
    listRunning: 'Checking what it can read',
    listDone: 'Checked what it can read',
    listFailed: "Couldn't check what it can read",
    readRunning: 'Reading connected data',
    readDone: 'Read connected data',
    readFailed: "Couldn't read connected data",
    namedRunning: 'Reading {name}',
    namedDone: 'Read {name}',
    namedFailed: "Couldn't read {name}",
    tablesCount: 'one:table|other:tables',
  },
  es: {
    listRunning: 'Comprobando qué puede leer',
    listDone: 'Comprobado qué puede leer',
    listFailed: 'No se pudo comprobar qué puede leer',
    readRunning: 'Leyendo datos conectados',
    readDone: 'Datos conectados leídos',
    readFailed: 'No se pudieron leer los datos conectados',
    namedRunning: 'Leyendo {name}',
    namedDone: 'Leído: {name}',
    namedFailed: 'No se pudo leer {name}',
    tablesCount: 'one:tabla|other:tablas',
  },
  et: {
    listRunning: 'Kontrollitakse, mida saab lugeda',
    listDone: 'Kontrollitud, mida saab lugeda',
    listFailed: 'Ei õnnestunud kontrollida, mida saab lugeda',
    readRunning: 'Loetakse ühendatud andmeid',
    readDone: 'Ühendatud andmed loetud',
    readFailed: 'Ühendatud andmeid ei õnnestunud lugeda',
    namedRunning: 'Loetakse: {name}',
    namedDone: 'Loetud: {name}',
    namedFailed: 'Ei õnnestunud lugeda: {name}',
    tablesCount: 'one:tabel|other:tabelit',
  },
  fi: {
    listRunning: 'Tarkistetaan, mitä voidaan lukea',
    listDone: 'Tarkistettu, mitä voidaan lukea',
    listFailed: 'Ei voitu tarkistaa, mitä voidaan lukea',
    readRunning: 'Luetaan yhdistettyjä tietoja',
    readDone: 'Yhdistetyt tiedot luettu',
    readFailed: 'Yhdistettyjä tietoja ei voitu lukea',
    namedRunning: 'Luetaan: {name}',
    namedDone: 'Luettu: {name}',
    namedFailed: 'Ei voitu lukea: {name}',
    tablesCount: 'one:taulukko|other:taulukkoa',
  },
  fr: {
    listRunning: 'Vérification des données lisibles',
    listDone: 'Données lisibles vérifiées',
    listFailed: 'Impossible de vérifier les données lisibles',
    readRunning: 'Lecture des données connectées',
    readDone: 'Données connectées lues',
    readFailed: 'Impossible de lire les données connectées',
    namedRunning: 'Lecture de {name}',
    namedDone: 'Lu : {name}',
    namedFailed: 'Impossible de lire {name}',
    tablesCount: 'one:table|other:tables',
  },
  ga: {
    listRunning: 'Ag seiceáil cad is féidir a léamh',
    listDone: 'Seiceáladh cad is féidir a léamh',
    listFailed: 'Níorbh fhéidir seiceáil cad is féidir a léamh',
    readRunning: 'Sonraí nasctha á léamh',
    readDone: 'Léadh sonraí nasctha',
    readFailed: 'Níorbh fhéidir sonraí nasctha a léamh',
    namedRunning: 'Á léamh: {name}',
    namedDone: 'Léadh: {name}',
    namedFailed: 'Níorbh fhéidir a léamh: {name}',
    tablesCount: 'one:tábla|two:thábla|few:thábla|many:dtábla|other:tábla',
  },
  hr: {
    listRunning: 'Provjera što se može čitati',
    listDone: 'Provjereno što se može čitati',
    listFailed: 'Nije moguće provjeriti što se može čitati',
    readRunning: 'Čitanje povezanih podataka',
    readDone: 'Povezani podaci pročitani',
    readFailed: 'Povezane podatke nije moguće pročitati',
    namedRunning: 'Čitanje: {name}',
    namedDone: 'Pročitano: {name}',
    namedFailed: 'Nije moguće pročitati: {name}',
    tablesCount: 'one:tablica|few:tablice|other:tablica',
  },
  hu: {
    listRunning: 'Annak ellenőrzése, mit olvashat',
    listDone: 'Ellenőrizve, mit olvashat',
    listFailed: 'Nem sikerült ellenőrizni, mit olvashat',
    readRunning: 'Csatlakoztatott adatok olvasása',
    readDone: 'Csatlakoztatott adatok beolvasva',
    readFailed: 'Nem sikerült beolvasni a csatlakoztatott adatokat',
    namedRunning: 'Olvasás: {name}',
    namedDone: 'Beolvasva: {name}',
    namedFailed: 'Nem sikerült beolvasni: {name}',
    tablesCount: 'one:tábla|other:tábla',
  },
  it: {
    listRunning: 'Verifica di cosa può leggere',
    listDone: 'Verificato cosa può leggere',
    listFailed: 'Impossibile verificare cosa può leggere',
    readRunning: 'Lettura dei dati collegati',
    readDone: 'Dati collegati letti',
    readFailed: 'Impossibile leggere i dati collegati',
    namedRunning: 'Lettura di {name}',
    namedDone: 'Letto: {name}',
    namedFailed: 'Impossibile leggere {name}',
    tablesCount: 'one:tabella|other:tabelle',
  },
  lt: {
    listRunning: 'Tikrinama, ką galima skaityti',
    listDone: 'Patikrinta, ką galima skaityti',
    listFailed: 'Nepavyko patikrinti, ką galima skaityti',
    readRunning: 'Skaitomi prijungti duomenys',
    readDone: 'Prijungti duomenys perskaityti',
    readFailed: 'Nepavyko perskaityti prijungtų duomenų',
    namedRunning: 'Skaitoma: {name}',
    namedDone: 'Perskaityta: {name}',
    namedFailed: 'Nepavyko perskaityti: {name}',
    tablesCount: 'one:lentelė|few:lentelės|many:lentelės|other:lentelių',
  },
  lv: {
    listRunning: 'Pārbauda, ko var nolasīt',
    listDone: 'Pārbaudīts, ko var nolasīt',
    listFailed: 'Neizdevās pārbaudīt, ko var nolasīt',
    readRunning: 'Nolasa savienotos datus',
    readDone: 'Savienotie dati nolasīti',
    readFailed: 'Neizdevās nolasīt savienotos datus',
    namedRunning: 'Nolasa: {name}',
    namedDone: 'Nolasīts: {name}',
    namedFailed: 'Neizdevās nolasīt: {name}',
    tablesCount: 'zero:tabulu|one:tabula|other:tabulas',
  },
  mt: {
    listRunning: 'Qed jiġi ċċekkjat x’jista’ jaqra',
    listDone: 'Ġie ċċekkjat x’jista’ jaqra',
    listFailed: 'Ma setax jiġi ċċekkjat x’jista’ jaqra',
    readRunning: 'Qed tinqara d-dejta konnessa',
    readDone: 'Id-dejta konnessa nqrat',
    readFailed: 'Id-dejta konnessa ma setgħetx tinqara',
    namedRunning: 'Qed jinqara: {name}',
    namedDone: 'Inqara: {name}',
    namedFailed: 'Ma setax jinqara: {name}',
    tablesCount: 'one:tabella|two:tabelli|few:tabelli|many:tabella|other:tabella',
  },
  nl: {
    listRunning: 'Controleren wat het kan lezen',
    listDone: 'Gecontroleerd wat het kan lezen',
    listFailed: 'Kon niet controleren wat het kan lezen',
    readRunning: 'Gekoppelde gegevens lezen',
    readDone: 'Gekoppelde gegevens gelezen',
    readFailed: 'Kon gekoppelde gegevens niet lezen',
    namedRunning: '{name} lezen',
    namedDone: '{name} gelezen',
    namedFailed: 'Kon {name} niet lezen',
    tablesCount: 'one:tabel|other:tabellen',
  },
  pl: {
    listRunning: 'Sprawdzanie, co można odczytać',
    listDone: 'Sprawdzono, co można odczytać',
    listFailed: 'Nie udało się sprawdzić, co można odczytać',
    readRunning: 'Odczytywanie połączonych danych',
    readDone: 'Odczytano połączone dane',
    readFailed: 'Nie udało się odczytać połączonych danych',
    namedRunning: 'Odczytywanie: {name}',
    namedDone: 'Odczytano: {name}',
    namedFailed: 'Nie udało się odczytać: {name}',
    tablesCount: 'one:tabela|few:tabele|many:tabel|other:tabeli',
  },
  pt: {
    listRunning: 'A verificar o que pode ler',
    listDone: 'Verificado o que pode ler',
    listFailed: 'Não foi possível verificar o que pode ler',
    readRunning: 'A ler dados ligados',
    readDone: 'Dados ligados lidos',
    readFailed: 'Não foi possível ler os dados ligados',
    namedRunning: 'A ler {name}',
    namedDone: 'Lido: {name}',
    namedFailed: 'Não foi possível ler {name}',
    tablesCount: 'one:tabela|other:tabelas',
  },
  ro: {
    listRunning: 'Se verifică ce poate citi',
    listDone: 'S-a verificat ce poate citi',
    listFailed: 'Nu s-a putut verifica ce poate citi',
    readRunning: 'Se citesc datele conectate',
    readDone: 'Datele conectate au fost citite',
    readFailed: 'Datele conectate nu au putut fi citite',
    namedRunning: 'Se citește: {name}',
    namedDone: 'Citit: {name}',
    namedFailed: 'Nu s-a putut citi: {name}',
    tablesCount: 'one:tabel|few:tabele|other:de tabele',
  },
  sk: {
    listRunning: 'Overovanie, čo sa dá čítať',
    listDone: 'Overené, čo sa dá čítať',
    listFailed: 'Nepodarilo sa overiť, čo sa dá čítať',
    readRunning: 'Čítanie pripojených údajov',
    readDone: 'Pripojené údaje prečítané',
    readFailed: 'Pripojené údaje sa nepodarilo prečítať',
    namedRunning: 'Čítanie: {name}',
    namedDone: 'Prečítané: {name}',
    namedFailed: 'Nepodarilo sa prečítať: {name}',
    tablesCount: 'one:tabuľka|few:tabuľky|many:tabuľky|other:tabuliek',
  },
  sl: {
    listRunning: 'Preverjanje, kaj je mogoče brati',
    listDone: 'Preverjeno, kaj je mogoče brati',
    listFailed: 'Ni bilo mogoče preveriti, kaj je mogoče brati',
    readRunning: 'Branje povezanih podatkov',
    readDone: 'Povezani podatki prebrani',
    readFailed: 'Povezanih podatkov ni bilo mogoče prebrati',
    namedRunning: 'Branje: {name}',
    namedDone: 'Prebrano: {name}',
    namedFailed: 'Ni bilo mogoče prebrati: {name}',
    tablesCount: 'one:tabela|two:tabeli|few:tabele|other:tabel',
  },
  sv: {
    listRunning: 'Kontrollerar vad den kan läsa',
    listDone: 'Kontrollerade vad den kan läsa',
    listFailed: 'Kunde inte kontrollera vad den kan läsa',
    readRunning: 'Läser ansluten data',
    readDone: 'Läste ansluten data',
    readFailed: 'Kunde inte läsa ansluten data',
    namedRunning: 'Läser {name}',
    namedDone: 'Läste {name}',
    namedFailed: 'Kunde inte läsa {name}',
    tablesCount: 'one:tabell|other:tabeller',
  },
};

for (const locale of Object.keys(CONNECTION_READ_COPY) as ActivityLocale[]) {
  if (locale === 'en') continue;
  const row = CONNECTION_READ_COPY[locale];
  LOCALIZED_ACTIVITY_VERBS[locale] = {
    ...LOCALIZED_ACTIVITY_VERBS[locale],
    // The unified search carries the existing company-work translation.
    search_context:
      LOCALIZED_ACTIVITY_VERBS[locale]?.search_context ??
      ACTIVITY_VERB_COPY[locale]?.find_company_work,
    list_connection_reads: { running: row.listRunning, done: row.listDone, failed: row.listFailed },
    read_connection: { running: row.readRunning, done: row.readDone, failed: row.readFailed },
  };
}

/** The connection-read copy for an app locale, English when it has none. */
export function connectionReadCopy(locale: string): ConnectionReadCopy {
  const normalized = locale.split('-')[0] as ActivityLocale;
  return CONNECTION_READ_COPY[normalized] ?? CONNECTION_READ_COPY.en;
}

/** A complete neutral fallback for registry entries without a translated row.
 * It is deliberately a fixed sentence per locale: activity copy must never
 * concatenate a translated verb stem with an English noun. */
const neutralActivityCopy: Record<Exclude<ActivityLocale, 'en'>, [string, string, string]> = {
  bg: ['Извършва се действие', 'Действието е извършено', 'Действието не можа да бъде извършено'],
  cs: ['Probíhá akce', 'Akce byla dokončena', 'Akci se nepodařilo dokončit'],
  da: ['Udfører handling', 'Handlingen er udført', 'Handlingen kunne ikke udføres'],
  de: ['Aktion wird ausgeführt', 'Aktion ausgeführt', 'Aktion konnte nicht ausgeführt werden'],
  el: ['Εκτελείται ενέργεια', 'Η ενέργεια ολοκληρώθηκε', 'Η ενέργεια δεν ολοκληρώθηκε'],
  es: ['Realizando una acción', 'Acción realizada', 'No se pudo realizar la acción'],
  et: ['Toiming on käimas', 'Toiming on lõpetatud', 'Toimingut ei õnnestunud lõpetada'],
  fi: ['Toiminto käynnissä', 'Toiminto valmis', 'Toimintoa ei voitu suorittaa'],
  fr: ['Exécution d’une action', 'Action effectuée', 'Impossible d’effectuer l’action'],
  ga: ['Gníomh ar siúl', 'Gníomh curtha i gcrích', 'Níorbh fhéidir an gníomh a chur i gcrích'],
  hr: ['Radnja u tijeku', 'Radnja je izvršena', 'Radnju nije moguće izvršiti'],
  hu: ['Művelet folyamatban', 'Művelet végrehajtva', 'A művelet nem sikerült'],
  it: ['Azione in corso', 'Azione eseguita', 'Impossibile eseguire l’azione'],
  lt: ['Vykdomas veiksmas', 'Veiksmas atliktas', 'Veiksmo atlikti nepavyko'],
  lv: ['Darbība tiek veikta', 'Darbība izpildīta', 'Darbību neizdevās izpildīt'],
  mt: ['Qed titwettaq azzjoni', 'L-azzjoni tlestiet', 'L-azzjoni ma setgħetx titlesta'],
  nl: ['Actie wordt uitgevoerd', 'Actie uitgevoerd', 'Actie kon niet worden uitgevoerd'],
  pl: ['Trwa wykonywanie działania', 'Działanie wykonane', 'Nie udało się wykonać działania'],
  pt: ['A executar uma ação', 'Ação executada', 'Não foi possível executar a ação'],
  ro: ['Se efectuează o acțiune', 'Acțiune efectuată', 'Acțiunea nu a putut fi efectuată'],
  sk: ['Prebieha akcia', 'Akcia dokončená', 'Akciu sa nepodarilo dokončiť'],
  sl: ['Dejanje poteka', 'Dejanje izvedeno', 'Dejanja ni bilo mogoče izvesti'],
  sv: ['Utför en åtgärd', 'Åtgärden är utförd', 'Åtgärden kunde inte utföras'],
};

const completeLocale = (locale: Exclude<ActivityLocale, 'en'>) =>
  Object.fromEntries(
    Object.keys(ACTIVITY_VERBS).map((key) => {
      const fallback = neutralActivityCopy[locale];
      return [key, { running: fallback[0], done: fallback[1], failed: fallback[2] }];
    }),
  ) as Record<string, ActivityVerb>;

for (const locale of Object.keys(neutralActivityCopy) as Array<Exclude<ActivityLocale, 'en'>>) {
  LOCALIZED_ACTIVITY_VERBS[locale] = {
    ...completeLocale(locale),
    ...LOCALIZED_ACTIVITY_VERBS[locale],
    // The unified search carries the existing company-work translation.
    search_context:
      LOCALIZED_ACTIVITY_VERBS[locale]?.search_context ??
      ACTIVITY_VERB_COPY[locale]?.find_company_work,
  };
}

export function localizedActivityVerb(
  locale: string,
  key: string,
  verb: ActivityVerb,
): ActivityVerb {
  const normalized = locale.split('-')[0] as ActivityLocale;
  const row = LOCALIZED_ACTIVITY_VERBS[normalized]?.[key];
  if (row) return { ...verb, ...row };
  return verb;
}

/** A complete local fallback for tools that are not in the presentation registry. */
export function fallbackActivityVerb(locale: string): ActivityVerb {
  const normalized = locale.split('-')[0] as ActivityLocale;
  const fallback = neutralActivityCopy[normalized as Exclude<ActivityLocale, 'en'>];
  if (fallback) return { running: fallback[0], done: fallback[1], failed: fallback[2] };
  return {
    running: 'Using tool',
    done: 'Used tool',
    failed: "Couldn't use tool",
  };
}

/**
 * A repeated step in the reader's grammar: the CLDR category picks the form and
 * the number goes where the form puts `{count}` ("Read 3 files", "Läste 3 filer").
 */
export function countedActivity(forms: string, count: number, locale: string): string {
  const byCategory = new Map(
    forms.split('|').map((form) => {
      const split = form.indexOf(':');
      return [form.slice(0, split), form.slice(split + 1)] as const;
    }),
  );
  let category: string = 'other';
  let number = String(count);
  try {
    category = new Intl.PluralRules(locale).select(count);
    number = new Intl.NumberFormat(locale).format(count);
  } catch {
    // An unknown locale still reads with its `other` form and plain digits.
  }
  const form = byCategory.get(category) ?? byCategory.get('other') ?? '';
  return form.replace('{count}', number);
}
